import dagre from 'dagre';
import produce from 'immer';

import pick from 'util/pick';

import {
  ANALYSIS_FLOWCHART_EDGE_LABEL_HEIGHT,
  ANALYSIS_FLOWCHART_EDGE_LABEL_WIDTH,
  ANALYSIS_FLOWCHART_NODE_HEIGHT,
  ANALYSIS_FLOWCHART_NODE_WIDTH,
} from '../AnalysisFlowchartStyleConstants';
import {AnalysisGraph} from './AnalysisGraphTypes';

/**
 * Compute positions to arrange the flowchart graph of an analysis.
 *
 * If `AnalysisFlowchart` is updated to have nodes of different sizes or edges with labels,
 * this algorithm must be complexified.
 *
 * @param unarrangedGraph An AnalysisFlowchart with uncomputed `position` values on the nodes
 * @returns An AnalysisFlowchart with updated `position` values on the nodes, to ensure an attractive layout
 */
export default function arrangeAnalysisGraph(unarrangedGraph: AnalysisGraph): AnalysisGraph {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setGraph({});
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  unarrangedGraph.nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      height: ANALYSIS_FLOWCHART_NODE_HEIGHT,
      width: ANALYSIS_FLOWCHART_NODE_WIDTH,
    });
  });
  unarrangedGraph.edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target, {
      // A more intelligent layout could calculate labels' actual widths, instead of using the max width
      height: ANALYSIS_FLOWCHART_EDGE_LABEL_HEIGHT,
      width: ANALYSIS_FLOWCHART_EDGE_LABEL_WIDTH,
    });
  });

  dagre.layout(dagreGraph);

  return {
    nodes: unarrangedGraph.nodes.map((node) =>
      produce(node, (draftNode) => {
        draftNode.position = pick(dagreGraph.node(node.id), 'x', 'y');
      })
    ),
    edges: unarrangedGraph.edges,
  };
}
